import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

const faqs = [
  {
    question: "What data analytics services do you offer?",
    answer:
      "We offer business intelligence dashboards, data visualization, predictive analytics, machine learning models, data warehousing, ETL pipelines, performance analytics, and data governance — tailored to your industry and goals.",
  },
  {
    question: "Which tools and platforms do you work with?",
    answer:
      "We work with Power BI, Tableau, Looker, Google BigQuery, AWS, Azure, Snowflake, Python, R, and custom stack integrations. We recommend the best fit based on your existing infrastructure and budget.",
  },
  {
    question: "How long does a typical analytics project take?",
    answer:
      "A basic BI dashboard can be delivered in 2–4 weeks. Full data warehouse and ML projects typically take 8–16 weeks depending on data complexity, integrations, and scope. We use agile sprints with regular deliverables.",
  },
  {
    question: "Can you work with our existing data sources?",
    answer:
      "Yes. We integrate with CRMs, ERPs, databases, APIs, spreadsheets, cloud storage, and third-party platforms. Our ETL pipelines unify disparate sources into a single analytics-ready dataset.",
  },
  {
    question: "Do you provide ongoing analytics support?",
    answer:
      "Absolutely. We offer managed analytics services including dashboard maintenance, model retraining, new data source integration, and dedicated analyst support on monthly retainers.",
  },
  {
    question: "Is my data secure with WebTech?",
    answer:
      "Security is a priority. We implement encryption, role-based access, audit logging, and compliance best practices. NDAs and data processing agreements are standard for all enterprise engagements.",
  },
];

const DaFaq: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <AnimatedSection as="section" className="da-section" aria-labelledby="da-faq-title">
      <div className="da-container da-faq__wrap">
        <SectionHeader
          eyebrow="FAQ"
          title="Frequently Asked Questions"
          text="Everything you need to know about our data analytics services."
        />

        <div className="da-faq__list">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <motion.div
                key={faq.question}
                className={`da-faq__item${isOpen ? " da-faq__item--open" : ""}`}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <button
                  type="button"
                  className="da-faq__trigger"
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                >
                  <span>{faq.question}</span>
                  <ChevronDown size={20} aria-hidden="true" />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      className="da-faq__answer"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <p>{faq.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </AnimatedSection>
  );
};

export default DaFaq;
